//Built-in libraries

//Third party libraries
import { PlusIcon } from '@heroicons/react/24/outline';
import { type ChangeEvent, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
//Custom components
import Button from '@/components/UI/Button';
import Input from '@/components/UI/Form/Input';
import Confirm from '@/components/UI/Modal/Confirm';
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
//Services
import { createInputChangedHandler } from '@/features/ApplicationForm/utils/formHandlers';
import { isObjectEmpty } from '@/utils';

//STP Libraries

//Assets and Miscellaneous
import { initialValues } from './constants';
import type { IMember } from './types';

//Component Begin

const OrganizationStaff = () => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editStaffId, setEditStaffId] = useState<string>('');
  const [selectedStaff, setSelectedStaff] = useState<IMember>();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<boolean>(false);
  const [staff, setStaff] = useState(initialValues);

  const { updateData, data } = useApplicationFormContext();

  const resetForm = () => {
    setStaff(initialValues);
  };

  const editStaffHandler = (f: any) => {
    setIsEditing(true);
    const formData = Object.keys(staff).reduce((prev, next) => {
      return {
        ...prev,
        [next]: {
          ...staff[next],
          value: f[next],
          isTouched: !!f[next],
        },
      };
    }, {});
    setStaff(formData);
    setEditStaffId(f.id);
  };

  const updateStaffHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(staff).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return {
            ...prev,
            [next]: staff[next].value,
            id: editStaffId,
          };
        },
        {}
      );
      updateData({
        ...data,
        step5: {
          ...data.step5,
          staffs: data.step5.staffs.map((p: any) => {
            if (p.id === editStaffId) {
              return formattedData;
            }
            return p;
          }),
        },
      });
      resetForm();
      setIsEditing(false);
    }
  };

  const removeStaffHandler = (staff: IMember) => {
    setShowRemoveConfirm(true);
    setSelectedStaff(staff);
  };

  const confirmRemoveStaff = async () => {
    setShowRemoveConfirm(false);
    const filteredStaffs = data.step5.staffs.filter((staff: any) => staff.id !== selectedStaff?.id);
    updateData({
      ...data,
      step5: {
        ...data.step5,
        staffs: filteredStaffs,
      },
    });

    if (selectedStaff?.id === editStaffId) {
      resetForm();
      setIsEditing(false);
    }
  };

  const cancelRemoveStaff = () => {
    setShowRemoveConfirm(false);
  };

  const validateForm = () => {
    const formData = { ...staff };

    const formDataErrors = Object.keys(formData).reduce((prev: { [key: string]: string }, next: string) => {
      if (formData[next].error.length >= 1) {
        return {
          ...prev,
          [next]: formData[next].error,
        };
      }
      if (!formData[next].isTouched && formData[next].rules.required) {
        setStaff((prevState) => ({
          ...prevState,
          [next]: { ...prevState[next], error: 'Required' },
        }));
        return {
          ...prev,
          [next]: 'Required',
        };
      }
      return { ...prev };
    }, {});

    return isObjectEmpty(formDataErrors);
  };

  const onAddStaffHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(staff).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return { ...prev, [next]: staff[next].value };
        },
        {}
      );
      updateData({
        ...data,
        step5: {
          ...data.step5,
          staffs: [...data.step5.staffs, { ...formattedData, id: uuidv4() }],
        },
      });
      resetForm();
    }
  };

  const inputChangedHandler = createInputChangedHandler(setStaff, staff);

  return (
    <>
      {showRemoveConfirm && (
        <Confirm
          title='Remove staff'
          message={`You are about to remove ${selectedStaff?.name} staff. Are you sure you want to remove ?`}
          acceptButtonLabel='Remove'
          declineButtonLabel='Cancel'
          accept={confirmRemoveStaff}
          decline={cancelRemoveStaff}
        />
      )}
      <section className='space-y-4'>
        <h3>Organizations Staff </h3>
        {data.step5?.staffs.length > 0 && (
          <div className='space-y-2 w-full h-full ring-1 ring-neutralColor-lighter overflow-hidden'>
            <table className='divide-y divide-gray-200 w-full'>
              <thead className='bg-gray-50'>
                <tr>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Name</th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Title</th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Duty</th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Length of Service in Position</th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'></th>
                </tr>
              </thead>
              <tbody className='bg-white divide-y divide-gray-200'>
                {data.step5.staffs.map((staff: IMember) => (
                  <tr key={staff.id} className={`${staff.id === editStaffId ? 'bg-neutralColor-lighter' : ''}`}>
                    <td className='p-4'>{staff.name}</td>
                    <td className='p-4'>{staff.title}</td>
                    <td className='p-4'>{staff.duties}</td>
                    <td className='p-4'>{staff.noOfServiceYears}</td>
                    <td className='flex justify-end gap-4 p-4'>
                      <div className='cursor-pointer hover:text-blue-500' onClick={() => editStaffHandler(staff)}>
                        Edit
                      </div>
                      <div className='cursor-pointer hover:text-tertiaryColor-normal' onClick={() => removeStaffHandler(staff)}>
                        Delete
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className='w-full grid grid-cols-6 gap-4'>
          <Input
            customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
            label='Name'
            id='name'
            name='name'
            type='text'
            value={staff.name.value}
            error={staff?.name?.error}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              inputChangedHandler(e.target.value, e.target.name);
            }}
            onBlur={(e: ChangeEvent<HTMLInputElement>) => {
              inputChangedHandler(e.target.value, e.target.name);
            }}
          />
          <Input
            customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
            label='Title'
            id='title'
            name='title'
            type='text'
            value={staff.title.value}
            error={staff?.title?.error}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              inputChangedHandler(e.target.value, e.target.name);
            }}
            onBlur={(e: ChangeEvent<HTMLInputElement>) => {
              inputChangedHandler(e.target.value, e.target.name);
            }}
          />
          <Input
            customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
            label='Duty'
            id='duties'
            name='duties'
            type='text'
            value={staff.duties.value}
            error={staff?.duties?.error}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              inputChangedHandler(e.target.value, e.target.name);
            }}
            onBlur={(e: ChangeEvent<HTMLInputElement>) => {
              inputChangedHandler(e.target.value, e.target.name);
            }}
          />
          <Input
            customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
            label='Length of Service in Position'
            id='noOfServiceYears'
            name='noOfServiceYears'
            type='text'
            value={staff.noOfServiceYears.value}
            error={staff?.noOfServiceYears?.error}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              inputChangedHandler(e.target.value, e.target.name);
            }}
            onBlur={(e: ChangeEvent<HTMLInputElement>) => {
              inputChangedHandler(e.target.value, e.target.name);
            }}
          />
        </div>
        {!isEditing && <Button icon={<PlusIcon />} label='Add' type='button' btnStyle='primary' onClick={onAddStaffHandler} />}
        {isEditing && (
          <div className='flex gap-4'>
            <Button label='Save' type='button' btnStyle='primary' onClick={updateStaffHandler} />
            <Button
              label='Cancel'
              type='button'
              btnStyle='neutral'
              onClick={() => {
                resetForm();
                setEditStaffId('');
                setIsEditing(false);
              }}
            />
          </div>
        )}
      </section>
    </>
  );
};

export default OrganizationStaff;
